import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import useIdleTimeout from "@/hooks/useIdleTimeout";
import IdleWarningModal from "@/components/IdleWarningModal";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

/**
 * `AuthProvider` — Bootstraps `/auth/me`, exposes { user, setUser, logout },
 * and enforces a 30-minute idle-logout policy for every authenticated
 * session (warning at 27m, hard logout at 30m). See docs at
 * `hooks/useIdleTimeout.js` for the activity-detection contract.
 */
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = loading, false = anon, object = user

    const refresh = useCallback(() => {
        return api
            .get("/auth/me")
            .then((r) => setUser(r.data))
            .catch(() => setUser(false));
    }, []);

    const logout = useCallback(async ({ reason } = {}) => {
        try {
            await api.post("/auth/logout");
        } catch {
            // ignore — the cookie will be cleared server-side or by TTL anyway
        }
        setUser(false);
        // SEC-003 (V1.6.7 audit): purge candidate-side draft / pending
        // stashes on logout so a shared-device attacker can't harvest a
        // prior user's CV text, drafted chat, or wishlist intent, and
        // can't have them auto-fire against the next user who signs in.
        try {
            const purgeKeys = [
                "daddytalk_pending_send",
                "pending_save_job",
                "cv_rate_pending",
                "cv_ref",
                "cv_src",
            ];
            purgeKeys.forEach((k) => localStorage.removeItem(k));
            sessionStorage.removeItem("cv_rate_pending");
            // Also drop any recruiter-specific draft stashes.
            Object.keys(localStorage)
                .filter((k) => k.startsWith("daddytalk_draft_"))
                .forEach((k) => localStorage.removeItem(k));
        } catch { /* storage may be unavailable in private mode */ }
        // Full reload avoids stale in-memory state polluting the login page.
        const suffix = reason ? `?reason=${encodeURIComponent(reason)}` : "";
        const isCandidate = window.location.pathname.startsWith("/candidate");
        window.location.replace(
            (isCandidate ? "/candidate/login" : "/login") + suffix,
        );
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const isAuthed = !!user && typeof user === "object";

    const { isWarning, remainingMs, stayActive } = useIdleTimeout({
        enabled: isAuthed,
        onTimeout: () => logout({ reason: "idle" }),
    });

    const value = { user, setUser, logout, refresh };
    return (
        <AuthContext.Provider value={value}>
            {children}
            {isAuthed && isWarning && (
                <IdleWarningModal
                    remainingMs={remainingMs}
                    onStay={stayActive}
                />
            )}
        </AuthContext.Provider>
    );
}
